import type { ReactElement } from 'react';
import { EntityAction, EntityFormActions, useEntity } from 'components';
import { openCatalogAction } from 'components/CatalogAction';
import type { ActivityType } from 'types';
import type { EmbeddedOutletOrServiceCategory } from '../outletOrServiceCategory/types';
import { useNavigate } from 'react-router';
import { useSessionScope } from 'services';

function productLocationUrl(
  slug: string,
  type: ActivityType,
  outlet: EmbeddedOutletOrServiceCategory,
  productId: number,
): string {
  const base = outlet.vendor
    ? `/${slug}/preferences/vendors/${outlet.vendor.id}/outlets/${outlet.id}/products/`
    : `/${slug}/preferences/service_categories/${outlet.id}/products/`;
  return `${base}${type}/${productId}`;
}

export function ProductActions({
  type,
  onClose,
  onChanged,
  onCopy,
  onRemoved,
  onMoved,
  inDialogFooter = false,
}: {
  type: ActivityType;
  /** Drawer mode: close the drawer after an action that leaves the drawer context. */
  onClose?: () => void;
  /** Refresh the owning collection after a catalogue mutation. */
  onChanged?: () => void;
  onCopy?: () => void;
  /** Page mode: navigate after product removal (`cascade` means dependents were removed too). */
  onRemoved?: (cascade: boolean) => void;
  /**
   * Stay-in-workspace MOVE handling (e.g. the price-list drawer): reconcile and
   * close instead of navigating to the product's new outlet. When absent, MOVE
   * navigates to the moved product's new location.
   */
  onMoved?: () => void;
  inDialogFooter?: boolean;
}): ReactElement {
  const ctx = useEntity();
  const { slug } = useSessionScope();
  const navigate = useNavigate();
  const actions: EntityAction<any>[] = [
    {
      action: 'catalog',
      permKey: 'change',
      button: {
        icon: 'list-tree',
        children: 'Catalog',
        variant: 'secondary',
        size: 'sm',
        onClick: () =>
          openCatalogAction(ctx, (selection) => {
            // Cancel/Escape resolves the modal without a selection.
            if (!selection) return;

            switch (selection.action) {
              case 'MOVE': {
                onChanged?.();
                if (onMoved) {
                  onMoved();
                  break;
                }
                const outletOrServiceCategory = selection.target as EmbeddedOutletOrServiceCategory;
                onClose?.();
                navigate(productLocationUrl(slug, type, outletOrServiceCategory, selection.id));
                break;
              }
              case 'CONVERT': {
                if (onChanged) {
                  onChanged();
                } else {
                  void ctx.mutate();
                }
                onClose?.();
                break;
              }
              case 'REMOVE': {
                onChanged?.();
                onClose?.();
                onRemoved?.(selection.cascade != null);
                break;
              }
              default: {
                throw new Error(`Action not handled: ${selection.action}`);
              }
            }
          }),
      },
    },
  ];

  if (onCopy) {
    actions.push({
      action: 'copy',
      permKey: 'add',
      button: {
        icon: 'copy',
        children: 'Copy',
        variant: 'secondary',
        size: 'sm',
        onClick: onCopy,
      },
    });
  }

  return <EntityFormActions ml={inDialogFooter ? '2' : '5'} actions={actions} />;
}
